import { RecipientUtils } from 'services';
import { CloudGroupUser, Group, Recipient } from 'types';
import { store } from 'reducers';

const GroupUtils = {
  /** Returns Groups whose hash contains the search string */
  filterGroups: (searchInput: string, groups: Group[]) =>
    !searchInput
      ? groups
      : groups.filter(({ hash = '' }) =>
          hash.includes(searchInput.toLowerCase()),
        ),

  /**
   * Returns boolean value based on if a group's list of recipients
   * is contained within the given list of Recipients.
   */
  groupInRecipients: (
    group: Group,
    recipients: Recipient[],
    isDedicated: boolean,
  ): Boolean => {
    const recipientIDs = RecipientUtils.getIds(recipients);
    if (isDedicated) {
      return group.recipients.every((recipient: Recipient) =>
        recipientIDs.includes(recipient.id),
      );
    } else {
      return (group.members as CloudGroupUser[]).every(
        (cloudGroupUser: CloudGroupUser) =>
          recipientIDs.includes(cloudGroupUser.email),
      );
    }
  },

  /**
   * Returns a transformed Group[] where the recipient Ids have been
   * replaced with full recipient objects and a hash is added.
   */
  groupUserTransform: (rawGroups: Group[]): Group[] => {
    const { hashGroup, sortByGroupName } = GroupUtils;
    const { matchRpnsToRecipient, sortByNameAndRoles } = RecipientUtils;
    const allRecipients = store.getState().recipients.addUsersItems;
    return rawGroups
      .map((rawGroup) => {
        const recipients = (rawGroup.members as string[])
          .map((member: string) => {
            return matchRpnsToRecipient(member, allRecipients);
          })
          .sort(sortByNameAndRoles);
        const hash = hashGroup(rawGroup as Group, recipients);

        return {
          ...rawGroup,
          hash,
          recipients,
        };
      })
      .sort(sortByGroupName);
  },

  groupCloudUserTransform: (rawGroups: Group[]): Group[] => {
    const { hashCloudGroup, sortByGroupName } = GroupUtils;

    return rawGroups.filter((rawGroup) => rawGroup.members !== null)
      .map((rawGroup) => {
        const hash = hashCloudGroup(rawGroup as Group);
        return {
          ...rawGroup,
          hash,
        };
      })
      .sort(sortByGroupName);
  },

  /** Create hash of group and recipient fields for text search */
  hashGroup: (group: Group, recipients: Recipient[]) => {
    const hash = [group.name.toLowerCase()];
    recipients.forEach((recipient: Recipient) => {
      hash.push((recipient.fullName || '').toLowerCase());
      hash.push((recipient.username || '').toLowerCase());
    });
    return hash.join(' ');
  },

  /** Create hash of group and recipient fields for text search */
  hashCloudGroup: (group: Group) => {
    const hash = [group.name.toLowerCase()];
    (group.members as CloudGroupUser[]).forEach(
      (cloudGroupUser: CloudGroupUser) => {
        hash.push((cloudGroupUser.username || '').toLowerCase());
        hash.push((cloudGroupUser.email || '').toLowerCase());
      },
    );
    return hash.join(' ');
  },
  /*
   * Returns a Recipient[] that contains all recipients
   * from every group in the Group[] given
   */
  recipientsFromGroups: (
    groups: Group[],
    isDedicated: boolean,
  ): Recipient[] | CloudGroupUser[] => {
    const recipients: Recipient[] | CloudGroupUser[] = [];

    groups.forEach((group: Group) => {
      if (isDedicated) {
        group.recipients.forEach((recipient: Recipient) => {
          recipients.push(recipient);
        });
      } else {
        (group.members as CloudGroupUser[]).forEach(
          (member: CloudGroupUser) => {
            (recipients as CloudGroupUser[]).push(member);
          },
        );
      }
    });
    return recipients;
  },

  /** Sorts groups by lowercase name */
  sortByGroupName: (a: Group, b: Group): number =>
    a.name.toLowerCase().trim() < b.name.toLowerCase().trim() ? -1 : 1,
};

export { GroupUtils };
